import { IAdsService } from "../../types/IAdsService";

import { PrebidProvider } from "../providers/prebid-provider";
import { JarvisService } from "../jarvis-service";
import { IBid, IProvider } from "../provider";
import { AmazonProvider } from "../providers/amazon-provider";
import { KeyValues } from "../keyvalues";
import { IConfig } from "../../types/IConfig";
import { FallbackProvider } from "../providers/fallback-provider";

const vgwTag = 'https://ams.cdn.arkadiumhosted.com/advertisement/video/vgw/vgw.xml';
const devTag = 'https://ams.cdn.arkadiumhosted.com/advertisement/video/static/advantage-skippable.xml';

export class WeSellService implements IAdsService {
  private iu: string;
  protected providers: IProvider[];
  protected jarvis: JarvisService;
  protected configured: Promise<any>;
  protected correlator = (Math.random()*1e15).toFixed(0);
  protected keyValues = new KeyValues(() => document.location.href, window.localStorage, document.cookie);
  protected config?: IConfig;
  public useH5?: boolean | undefined;

  constructor() {
    const { hostname } = window.location;
    this.iu = `/100151972/${hostname}`;
    this.providers = [ new PrebidProvider(this.keyValues), new AmazonProvider(this.keyValues), new FallbackProvider(this.keyValues) ];
    this.jarvis = new JarvisService(hostname);
    this.configured = this.jarvis.fetch().then((config) => {
      this.useH5 = config.config.isH5Enabled;
      this.iu = `/100151972${config.mcm ? ',' + config.mcm : ''}/${hostname}`;
      return Promise.allSettled(this.providers.map(p => p.configure(config, this.iu)));
    });
  }

  public request(config: IConfig): Promise<IBid[]> {
    this.keyValues.setFromConfig(config.keyvalues);
    this.keyValues.setKeyValue('product', config.product || 'unknown');
    this.keyValues.setKeyValue('elementId', config.type || config.position || 'pre-roll');

    const isDev = false;
    const duration = config.duration || 30;
    const { href } = document.location;

    if (href.includes('ark_preroll_test=true')) {
      return Promise.resolve([{ bid: { cpm: 0 }, provider: this.createStubProvider(devTag) }]);
    }

    if (href.includes('ark_force_nobid=true')) {
      return Promise.resolve([]);
    }

    if (href.includes('games.vgwplay.com')) {
      return Promise.resolve([{ bid: { cpm: 0 }, provider: this.createStubProvider(vgwTag) }]);
    }

    if (isDev) {
      return Promise.resolve([{ bid: { cpm: 0 }, provider: this.createStubProvider(devTag) }]);
    }

    const keyValues = this.keyValues.getObject({ retryOnError: '0' });

    return this.configured
      .then(() => Promise.allSettled(this.providers.map(p => p.requestBids({ duration, keyValues, ppid: this.keyValues.getPPID() }))))
      .then((promises) => {
        const sortedBids = promises
          .flatMap(p => p.status === 'fulfilled' ? p.value: [])
          .sort((a, b) => b?.bid?.cpm - a?.bid?.cpm);
        return sortedBids;
      }).catch(() => []);
  }

  public cleanup() {
    return this.providers.forEach(p => p.cleanup());
  }

  protected getPPID() {
    try {
      return localStorage.getItem('_ppid');
    } catch (_) {
      return;
    }
  }

  protected createStubProvider(url: string): IProvider {
    const noImaUrl = 'https://optimized-by.rubiconproject.com/a/api/vast.xml?account_id=17214&site_id=161772&zone_id=775734&size_id=201&p_aso.video.protocols=2,5,3,6,7,8&p_aso.video.maxduration=30&p_aso.video.mimes="application/javascript","video/mp4"&p_aso.video.playbackmethod=3&p_aso.video.api=2';
    return {
      name: 'null',
      configure() {},
      requestBids: () => Promise.resolve([]),
      buildVideoUrl: (bid, noIma) => Promise.resolve(noIma ? noImaUrl : url),
      cleanup: () => null,
    };
  }
}
